import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

/** How often `useNow` re-reads the clock. */
export const NOW_TICK_MS = 60_000;

/**
 * A clock that keeps moving after mount.
 *
 * `useMemo(() => now ?? new Date(), [now])` reads the clock exactly once, so a
 * queue left open overnight — the normal state of a working queue — kept
 * yesterday's range and yesterday's idea of which consults had already
 * happened, until someone reloaded the page (BAR-2708). A minute is fine
 * granularity for everything these queues derive from the clock: "3 days
 * ago", "occurred yet", "overdue".
 *
 * Anything keyed on the returned value re-derives once a minute, so a query
 * variable must be keyed on `useCalendarDay` instead, or it refetches every
 * tick.
 *
 * @param override - A fixed clock, for tests. Ticking is off while it is set.
 */
export function useNow(override?: Date, tickMs = NOW_TICK_MS): Date {
  const [now, setNow] = useState(() => override ?? new Date());

  useEffect(() => {
    if (override) {
      return undefined;
    }

    const id = window.setInterval(() => setNow(new Date()), tickMs);
    return () => window.clearInterval(id);
  }, [override, tickMs]);

  return override ?? now;
}

/**
 * Local midnight of `now`'s calendar day, with a stable identity until the day
 * changes. The right key for anything sent to the server once a day — a
 * preset's date range, a "this week" window — off a clock that ticks by the
 * minute.
 */
export function useCalendarDay(now: Date): Date {
  const dayKey = dayjs(now).format('YYYY-MM-DD');
  return useMemo(() => dayjs(dayKey).toDate(), [dayKey]);
}
